import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchSession, SessionResponse } from '../../services/testSessionService';

// State interface
interface SessionState {
  currentSession: SessionResponse | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// Initial state
const initialState: SessionState = {
  currentSession: null,
  status: 'idle',
  error: null
};

// Async thunk for fetching session data
export const fetchSessionData = createAsyncThunk(
  'session/fetchSessionData',
  async (sessionId: string) => {
    const session = await fetchSession(sessionId);
    if (!session) {
      throw new Error('Failed to fetch session data');
    }
    return session;
  }
);

// Create the slice
const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    clearSession: (state) => {
      state.currentSession = null;
      state.status = 'idle';
      state.error = null;
    },
    setSession: (state, action: PayloadAction<SessionResponse>) => {
      state.currentSession = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    updateSessionAnswer: (state, action: PayloadAction<{ questionId: string; answer: number }>) => {
      if (state.currentSession && state.currentSession.answers) {
        const { questionId, answer } = action.payload;
        const existingAnswerIndex = state.currentSession.answers.findIndex(
          answerRecord => answerRecord.questionId === questionId
        );
        
        if (existingAnswerIndex >= 0) {
          // Update existing answer
          state.currentSession.answers[existingAnswerIndex] = {
            ...state.currentSession.answers[existingAnswerIndex],
            answer,
            timestamp: new Date().toISOString()
          };
        } else {
          // Add new answer
          state.currentSession.answers.push({
            questionId,
            answer,
            timestamp: new Date().toISOString()
          });
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionData.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSessionData.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentSession = action.payload;
        state.error = null;
      })
      .addCase(fetchSessionData.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch session';
        state.currentSession = null;
      });
  }
});

export const { clearSession, setSession, updateSessionAnswer } = sessionSlice.actions;
export default sessionSlice.reducer;

// Selectors
export const selectCurrentSession = (state: { session: SessionState }) => state.session.currentSession;
export const selectSessionStatus = (state: { session: SessionState }) => state.session.status;
export const selectSessionError = (state: { session: SessionState }) => state.session.error;